import { usePostHog } from '@posthog/react';
import { useOpenedFromCreateDialog } from 'components/create-dialog/CreateDialogContext';
import RadioInput from 'components/forms/inputs/rhf-inputs/RadioInput';
import useAuth from 'hooks/useAuth';
import { useFuzzySearch } from 'hooks/useFuzzySearch';
import { blocksToForm } from 'pages/contacts/blocking/BlockingPolicyReadonlyShared';
import { mapToCompanyDto, mapToPersonDto } from 'pages/contacts/ContactPageForm';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useAddContactMutation } from 'store/api/contacts';
import { useIsEinvoicingEnabled } from 'utils/useIsEinvoicingEnabled';
import { ContactDto, ContactForm, PartyType, RelationshipTypeCode } from 'types/contacts';
import { errorAlert } from 'utils/alert';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { ModalFooter } from '../ModalFooter';
import { ModalHeader } from '../ModalHeader';
import { AddPartyCompanyForm } from './AddPartyCompanyForm';
import { AddPartyPersonForm } from './AddPartyPersonForm';
import { Setting } from 'types/back-office-settings';
import { useSettings } from 'utils/useSettings';

interface Props {
    onClose: () => void;
    onContactAdd: (newValue: ContactDto) => void;
    contactType: PartyType;
    preSelectedEmployer?: { id: number; name: string; partyType: PartyType };
    preSelectedEmployee?: { id: number; name: string; partyType: PartyType };
    initialData?: ContactForm;
    /** What the user typed while looking for the contact, so it need not be typed again. */
    initialName?: string;
    connectedContactsGroupName?: string;
    requireEmail?: boolean;
    isCorrespondence?: boolean;
}

const USED_CLASSIFIERS = [
    'RELATIONSHIP_TYPE',
    'PERSON_TITLE',
    'COUNTRY',
    'BUSINESS_AREA_TYPE',
    'LANGUAGE',
    'MARKETING_AREA_TYPE',
];

/** The inline styles a caller sizes this popup's panel with (`FORM_OVERFLOW_LARGE_MODAL_STYLE`). */
const PANEL_SIZE = ['width', 'max-width', 'height', 'max-height'];

/** A person's typed name is read as first names and a last name, the last word being the last name. */
function splitTypedName(contactType: PartyType, typed: string) {
    const name = typed.trim();
    if (contactType === PartyType.COMPANY) {
        return { firstName: '', lastName: '', officialName: name };
    }
    const lastSpace = name.lastIndexOf(' ');
    return lastSpace < 0
        ? { firstName: '', lastName: name, officialName: '' }
        : { firstName: name.slice(0, lastSpace).trim(), lastName: name.slice(lastSpace + 1), officialName: '' };
}

const AddContactModal = (props: Props) => {
    const {
        onClose,
        onContactAdd,
        contactType,
        preSelectedEmployer,
        preSelectedEmployee,
        initialData,
        initialName = '',
        connectedContactsGroupName,
        requireEmail,
        isCorrespondence = false,
    } = props;
    const posthog = usePostHog();
    const openedFromCreateDialog = useOpenedFromCreateDialog();
    const bodyRef = useRef<HTMLDivElement>(null);
    const { user: currentUser } = useAuth();
    const isContactDefaultStatusClient = useSettings().isSettingEnabled(Setting.NEW_CONTACTS_DEFAULT_TO_CLIENT);
    const groupSortOrder = connectedContactsGroupName ? [connectedContactsGroupName] : undefined;
    const typedName = splitTypedName(contactType, initialName);
    const defaultValues: ContactForm = {
        partyType: contactType,
        relationshipType: isContactDefaultStatusClient ? RelationshipTypeCode.CLIENT : RelationshipTypeCode.NON_CLIENT,
        name: initialName.trim(),
        officialLocalName: '',
        position: '',
        mainEmail: '',
        mainPhone: '',
        mainMobile: '',
        statusCode: 'ACTIVE',
        clientReference: '',
        preferredLanguage: null,
        billingNotes: '',
        invoicingInfo: '',
        debtNotes: '',
        debtExplanation: '',
        debtHistory: '',
        notes: '',
        coiNotes: '',
        businessAreas: [],
        relatedKeywords: [],
        managers: [],
        partners: [],
        administrators: [],
        businessContacts: currentUser ? [{ id: currentUser.id, name: currentUser.name }] : [],
        paymentNotificationReceivers: [],
        billingAddress: null,
        billingLocalAddress: null,
        personalCode: '',
        personTitle: null,
        personManualTitle: '',
        linkedinUrl: '',
        homeAddress: null,
        homeLocalAddress: null,
        firstName: typedName.firstName,
        lastName: typedName.lastName,
        localFirstName: '',
        localLastName: '',
        officialName: typedName.officialName,
        regCode: '',
        // This modal collects no VAT fields, so it keeps the party not VAT liable.
        notVatLiable: true,
        vatRegNum: '',
        vatCountryCode: null,
        website: '',
        einvoiceAddress: '',
        einvoiceOperatorCode: '',
        einvoiceOperatorType: null,
        employees: preSelectedEmployee ? [{ name: preSelectedEmployee.name, id: preSelectedEmployee.id }] : [],
        employedBy: preSelectedEmployer ? [{ name: preSelectedEmployer.name, id: preSelectedEmployer.id }] : [],
        primaryContact: null,
        primaryContactForCompanies: [],
        partyGroups: [],
        registrationAddress: null,
        registrationLocalAddress: null,
        visitingAddress: null,
        correspondenceAddress: null,
        segmentType: null,
        serviceLevelType: null,
        payerParty: null,
        useJointInvoicing: false,
        issueInvoicesWithCurrentDate: false,
        isInternalClient: false,
        primaryRecipientParty: null,
        primaryRecipientAdditionalEmails: '',
        secondaryRecipientParties: [],
        secondaryRecipientAdditionalEmails: '',
        invoiceArchiveEmail: '',
        invoiceTemplate: null,
        invoiceSpecTemplate: null,
        isSpecIncludedInInvoice: null,
        invoiceCustomization: null,
        dmsLink: '',
        isPoliticallyExposedPerson: false,
        identityDocumentNumber: null,
        identityDocumentDateOfIssue: null,
        identityDocumentDateOfExpiry: null,
        identityDocumentIssuingCountryCode: null,
        identityDocumentIssuingAuthority: null,
        invoicingDeadlineDay: null,
        invoicingPaymentDays: null,
        preferredBankAccount: null,
        externalBillingInfo: null,
        isPersonaNonGrata: false,
        riskProfileTypeCode: null,
        riskProfileNotes: '',
        complianceVerificationStatusTypeCode: null,
        complianceVerificationNotes: '',
        preferredMailingLanguageTypeCode: null,
        countries: [],
        mailingPreferences: [],
        marketingAreaTypes: [],
        cooperationAreaTypes: [],
        isServiceAgreementNotNeeded: false,
        isUboNotNeeded: false,
        changeReasons: null,
        blocking: blocksToForm(null),
    };

    const { handleSubmit, control, setValue, getValues, getFieldState } = useForm<ContactForm>({
        defaultValues: initialData ? initialData : defaultValues,
    });

    // Need to track these fields for dynamic form display.
    const [partyType, relationshipType, watchedName, einvoiceOperatorType] = useWatch({
        control,
        name: ['partyType', 'relationshipType', 'name', 'einvoiceOperatorType'],
    });

    const [addContact] = useAddContactMutation();
    const { data: isEinvoicingEnabled } = useIsEinvoicingEnabled();

    const { classifiers } = useClassifiers({ codes: USED_CLASSIFIERS });
    const fuzzySearch = useFuzzySearch({ partyType });
    const { searchHandlers } = fuzzySearch;

    useEffect(() => {
        if (isEinvoicingEnabled && partyType === PartyType.COMPANY && !einvoiceOperatorType) {
            setValue('einvoiceOperatorType', {
                value: 'FINVOICE',
                label: t('einvoiceOperatorType.FINVOICE'),
            });
        }
    }, [einvoiceOperatorType, isEinvoicingEnabled, partyType, setValue]);

    // Permission checks for relationship type options
    const hasCreatePartiesPermission = useHasPermissions(['CREATE.PARTIES']);
    const hasCreateClientsPermission = useHasPermissions(['CREATE.PARTIES.CLIENTS']);
    const hasCreateLeadsNonClientsPermission = useHasPermissions(['CREATE.PARTIES.LEADS_AND_NON_CLIENTS']);

    const relationshipTypeOptions = useMemo(() => {
        const options = (
            (getValidOptionsFromClassifier(classifiers['RELATIONSHIP_TYPE']) as {
                label: string;
                value: string;
                disabled: boolean;
            }[]) ?? []
        ).map((option) => {
            let disabled = false;

            if (!hasCreatePartiesPermission) {
                if (option.value === RelationshipTypeCode.CLIENT && !hasCreateClientsPermission) {
                    disabled = true;
                } else if (
                    (option.value === RelationshipTypeCode.LEAD || option.value === RelationshipTypeCode.NON_CLIENT) &&
                    !hasCreateLeadsNonClientsPermission
                ) {
                    disabled = true;
                }
            }

            return { ...option, disabled };
        });

        const sortOrder = [RelationshipTypeCode.CLIENT, RelationshipTypeCode.NON_CLIENT, RelationshipTypeCode.LEAD];
        return options.sort((a, b) => {
            const aIndex = sortOrder.indexOf(a.value as RelationshipTypeCode);
            const bIndex = sortOrder.indexOf(b.value as RelationshipTypeCode);
            return aIndex - bIndex;
        });
    }, [classifiers, hasCreatePartiesPermission, hasCreateClientsPermission, hasCreateLeadsNonClientsPermission]);

    const onSave = async () => {
        await handleSubmit((data) => handleContactCreate(data))();
    };

    const handleContactCreate = async (formData: ContactForm) => {
        try {
            const data = partyType === 'PERSON' ? mapToPersonDto(formData) : mapToCompanyDto(formData);
            const res = await addContact({ ...data, quickAdd: true }).unwrap();
            posthog.capture('contact_created', {
                relationship_type: formData.relationshipType,
                party_type: partyType === PartyType.PERSON ? 'person' : 'company',
            });
            onContactAdd(res);
            onClose();
        } catch (error) {
            console.error('Save contact error: ', error);
            if (error instanceof Error) {
                errorAlert(t('contacts.error-creating-contact', { message: error?.message }));
            }
        }
    };

    // Prefill company name based on official name if its not already set
    const prefillName = (val: string | undefined | null) => {
        const name = getValues('name');
        if (!name || name?.length < 1) {
            setValue('name', val ?? '');
        }
    };

    // Prefill person informal name based on first name and last name if its not already set
    const prefillInformalName = useCallback(
        (value: string | undefined | null, composedWith: 'firstName' | 'lastName') => {
            if (value && value.trim().length) {
                const isNameTouched = getFieldState('name').isTouched;
                const name = getValues('name');
                if (!name || !name.length || !isNameTouched) {
                    const secondPartOfCompositeValue = getValues(composedWith);
                    if (!secondPartOfCompositeValue || !secondPartOfCompositeValue.length) {
                        setValue('name', value.trim());
                    } else {
                        setValue(
                            'name',
                            composedWith === 'lastName'
                                ? `${value.trim()} ${secondPartOfCompositeValue}`
                                : `${secondPartOfCompositeValue} ${value.trim()}`
                        );
                    }
                }
            }
        },
        [getFieldState, getValues, setValue]
    );

    // Trigger fuzzy search when name field changes
    useEffect(() => {
        if (searchHandlers?.name && watchedName && watchedName.trim().length > 2) {
            const syntheticEvent = {
                target: {
                    value: watchedName,
                },
            } as React.ChangeEvent<HTMLInputElement>;
            searchHandlers.name.handler(syntheticEvent);
        }
    }, [watchedName, searchHandlers]);

    // Above the create dialog this takes the popup's standard panel, whatever size its caller asked for, so the
    // dialog stays in view. The caller's size is inline on the panel, which the popup stack renders: it is lifted
    // off while this is in the panel and put back after, since React rewrites only the inline styles that
    // changed and would leave the next popup in the same panel without its size.
    useLayoutEffect(() => {
        const panel = openedFromCreateDialog
            ? bodyRef.current?.closest<HTMLElement>('[data-slot="dialog-content"]')
            : null;
        if (!panel) {
            return undefined;
        }
        const asked = PANEL_SIZE.map((property) => panel.style.getPropertyValue(property));
        PANEL_SIZE.forEach((property) => panel.style.removeProperty(property));
        return () => PANEL_SIZE.forEach((property, index) => panel.style.setProperty(property, asked[index]));
    }, [openedFromCreateDialog]);

    return (
        <>
            <ModalHeader title={t('contact-person-modal.title')} onCancel={onClose} />
            <div ref={bodyRef} className="h-full overflow-y-auto px-8 py-[38px] max-sm:px-4 max-sm:py-6">
                <div className="flex flex-col gap-4">
                    <RadioInput
                        setValue={setValue}
                        control={control}
                        label="contacts.about.relationship-type"
                        name="relationshipType"
                        options={relationshipTypeOptions}
                        getOptionLabel="label"
                        getOptionIdentifier="value"
                        getOptionDisabled="disabled"
                    />
                    {partyType === PartyType.PERSON ? (
                        <AddPartyPersonForm
                            control={control}
                            classifiers={classifiers}
                            setValue={setValue}
                            groupSortOrder={groupSortOrder}
                            requireEmail={requireEmail}
                            prefillInformalName={prefillInformalName}
                            relationshipType={relationshipType}
                            fuzzySearch={fuzzySearch}
                            isCorrespondence={isCorrespondence}
                        />
                    ) : (
                        <AddPartyCompanyForm
                            control={control}
                            classifiers={classifiers}
                            setValue={setValue}
                            requireEmail={requireEmail}
                            prefillName={prefillName}
                            relationshipType={relationshipType}
                            fuzzySearch={fuzzySearch}
                            isCorrespondence={isCorrespondence}
                        />
                    )}
                </div>
            </div>
            <ModalFooter
                onCancel={onClose}
                onSave={() => {
                    void onSave();
                }}
            />
        </>
    );
};

export default AddContactModal;
